import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getJournals, getArticleById, getVerboseIssues } from '../services/firebaseService';
import { formatIssueTitle, formatIssuePeriod } from '../utils/issueFormat';
import { vancouverCitation } from '../utils/citation';
import { formatDisplayDate } from '../utils/pdfDates';
import { getArticlePages, formatArticlePages } from '../utils/articlePages';
import assetService from '../services/assetService';
import { getBestFileUrl } from '../utils/fileUrlConverter';
import { usePageContent, getPublisherInfo } from '../services/pageContentService';
import { parseParamNumber } from '../utils/journalUrlHelper';
import { getJournalTypeDisplayName } from '../config/journalTypes';
import {
  FaFilePdf,
  FaDownload,
  FaArrowLeft,
  FaQuoteLeft,
  FaCopy,
  FaCheck,
} from 'react-icons/fa';

// Abstracts pasted from a PDF have a line break at the end of every line. Blank lines (or a new
// "Heading:" such as "Objective:") start a paragraph; other line breaks are joined with a space,
// so the text can be justified.
const abstractParagraphs = (text) =>
  String(text || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n(?=[A-Z][A-Za-z ]{2,30}:)/g, '\n\n')
    .split(/\n\s*\n/)
    .map((para) => para.replace(/\s*\n\s*/g, ' ').replace(/\s{2,}/g, ' ').trim())
    .filter(Boolean);

const ArticleDetail = () => {
  const { volParam, issParam, articleId } = useParams();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { content: aboutContent } = usePageContent('about_us');
  const [issues, setIssues] = useState([]);
  const [citationCopied, setCitationCopied] = useState(false);

  useEffect(() => {
    getVerboseIssues().then(setIssues).catch(() => {});
  }, []);
  const publisher = getPublisherInfo(aboutContent);

  // Extract clean volume and issue numbers from route params
  const volNumber = parseParamNumber(volParam) || '1';
  const issNumber = parseParamNumber(issParam) || '1';

  // Period of this article's issue: the issue record from the admin, else the article's own
  const issuePeriod = useMemo(() => {
    const num = (v) => String(v ?? '').replace(/\D/g, '');
    const record = issues.find((i) => num(i.Vol) === String(volNumber) && num(i.issue) === String(issNumber));
    return formatIssuePeriod(record?.validity || article?.validity) || 'January–June 2026';
  }, [issues, volNumber, issNumber, article]);

  // Load journals list and locate article
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const loadData = async () => {
      try {
        // Always read the saved articles so admin edits (abstract, title, PDF...) show straight away;
        // the session cache is only used if loading fails
        let list;
        try {
          list = await getJournals();
          assetService.setCache('journals', list);
        } catch (e) {
          list = assetService.getCache('journals') || [];
        }

        if (!isMounted) return;

        const targetVolNum = parseParamNumber(volParam);
        const targetIssNum = parseParamNumber(issParam);
        const cleanArticleIdParam = String(articleId || '').toLowerCase().trim();

        // Filter articles belonging to this volume and issue
        const issueArticles = (list || []).filter((item) => {
          const v = parseParamNumber(item.volume || item.Vol);
          const i = parseParamNumber(item.issue || item.Issue);
          const volOk = !targetVolNum || !v || targetVolNum === v;
          const issOk = !targetIssNum || !i || targetIssNum === i;
          return volOk && issOk;
        });

        let matched = null;

        // 1. Match by articleNumber
        const byArticleNumberIdx = issueArticles.findIndex(
          (item) => String(item.articleNumber || '').toLowerCase() === cleanArticleIdParam
        );
        if (byArticleNumberIdx !== -1) {
          matched = issueArticles[byArticleNumberIdx];
        }

        // 2. If articleId is numeric (e.g. "1", "2", "5"), match by 1-based index in this issue
        if (!matched && /^\d+$/.test(cleanArticleIdParam)) {
          const num = parseInt(cleanArticleIdParam, 10);
          if (num > 0 && num <= issueArticles.length) {
            matched = issueArticles[num - 1];
          }
        }

        // 3. Match by direct Firestore document ID
        if (!matched) {
          const byIdIdx = (list || []).findIndex(
            (item) => String(item.id || '').toLowerCase() === cleanArticleIdParam
          );
          if (byIdIdx !== -1) {
            matched = list[byIdIdx];
          }
        }

        // 4. Match by slugified title if needed
        if (!matched) {
          const bySlugIdx = issueArticles.findIndex((item) => {
            if (!item.title) return false;
            const slug = item.title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '')
              .substring(0, 40);
            return slug === cleanArticleIdParam;
          });
          if (bySlugIdx !== -1) {
            matched = issueArticles[bySlugIdx];
          }
        }

        // 5. Fallback: query Firestore directly by ID if not in list
        if (!matched && articleId) {
          const directDoc = await getArticleById(articleId);
          if (directDoc) {
            matched = directDoc;
          }
        }

        // 6. Absolute fallback: use the first article in the issue if nothing else matched
        if (!matched && issueArticles.length > 0) {
          matched = issueArticles[0];
        }

        if (isMounted) {
          setArticle(matched);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load article detail:', err);
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [volParam, issParam, articleId]);

  // Direct file URL
  const fileUrl = useMemo(() => {
    if (!article) return null;
    let url = getBestFileUrl(article);
    if (!url && article.file?.name) {
      url = `/files/${article.file.name}`;
    }
    return url || '/files/sample-article.pdf';
  }, [article]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-[#03478E] border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 font-medium text-sm">Loading scholarly article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col items-center justify-center py-20 px-4 text-center">
        <div className="bg-white rounded-3xl p-8 max-w-lg shadow-sm">
          <h2 className="section-heading mb-3">Article Not Found</h2>
          <p className="text-gray-600 text-sm mb-6">
            The requested article could not be located in Volume {volNumber}, Issue {issNumber}.
          </p>
          <Link
            to="/current-issue"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#03478E] text-white hover:text-white text-xs font-bold rounded-full uppercase tracking-wider hover:bg-[#023366]"
          >
            <span>Back to Issue</span>
          </Link>
        </div>
      </div>
    );
  }

  const articleTypeDisplay = article.type ? getJournalTypeDisplayName(article.type) : 'Review Article';
  const authorDisplay = article.authors
    ? article.authors.startsWith('Dr.')
      ? article.authors
      : `Dr. ${article.authors}`
    : 'Dr. AIMJHM Author';

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">

      {/* ================= BREADCRUMB BAR (Screen 6 Mockup) ================= */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center flex-wrap gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          {article?.isCurrentIssue !== false ? (
            <>
              <Link to="/current-issue" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
                Current Issue
              </Link>
              <span>&gt;</span>
            </>
          ) : (
            <>
              <Link to="/archives" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
                Archives
              </Link>
              <span>&gt;</span>
              <span className="text-gray-500">
                {formatIssueTitle(volNumber, issNumber, issuePeriod)}
              </span>
              <span>&gt;</span>
            </>
          )}
          <span className="font-semibold text-gray-700">Article</span>
        </div>
      </div>

      {/* ================= MAIN 2-COLUMN LAYOUT - NO HARSH BORDERS, ROUNDED-3XL ================= */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">

          {/* ================= LEFT MAIN CONTENT (~68% width) ================= */}
          <main className="lg:col-span-8 bg-white rounded-3xl p-8 sm:p-10 shadow-sm space-y-8">

            {/* Top Category Badge */}
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-wider px-3.5 py-1 rounded-full text-[#03478E] bg-blue-50/80">
                {articleTypeDisplay}
              </span>
            </div>

            {/* Article Title (Large Serif Bold) */}
            <h1 className="page-title">
              {article.title}
            </h1>

            {/* Author Name */}
            <div>
              <p className="sub-heading">
                <span className="font-sans font-semibold text-gray-500">Author(s): </span>
                {authorDisplay}
              </p>
            </div>

            {/* View Full Article PDF Action Button */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
              <a
                href={fileUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-[#03478E] hover:bg-[#023366] text-white hover:text-white text-sm font-bold rounded-full uppercase tracking-wider transition-all duration-200 shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <FaFilePdf className="text-base" />
                <span>VIEW FULL ARTICLE PDF</span>
              </a>

              <Link
                to={article?.isCurrentIssue !== false ? "/current-issue" : "/archives"}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#03478E] hover:text-[#023366] hover:underline"
              >
                <FaArrowLeft className="text-xs" />
                <span>Back to Issue</span>
              </Link>
            </div>

            {/* Abstract Block */}
            <section className="pt-2 space-y-3">
              <h2 className="section-heading">
                {article.type === 'editorialNote' ? 'Editorial Note' : 'Abstract'}
              </h2>
              {article.abstract ? (
                <div className="body-text font-sans text-justify hyphens-auto bg-gray-50/50 p-6 rounded-2xl space-y-3">
                  {abstractParagraphs(article.abstract).map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic bg-gray-50/50 p-6 rounded-2xl">
                  Full abstract and content available in the downloadable PDF.
                </div>
              )}
            </section>

            {/* Keywords Block */}
            {article.keywords && (
              <section className="pt-2 space-y-2">
                <h2 className="section-heading">
                  Keywords
                </h2>
                <p className="body-text font-sans bg-blue-50/40 p-4 rounded-2xl">
                  {article.keywords}
                </p>
              </section>
            )}


            {/* Bottom Download Full Article PDF Button */}
            <div className="pt-4">
              <a
                href={fileUrl || '#'}
                download={`${(article.title || 'article').replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-[#03478E] hover:bg-[#023366] text-white hover:text-white text-sm font-bold rounded-full uppercase tracking-wider transition-all duration-200 shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <FaDownload className="text-base" />
                <span>DOWNLOAD FULL ARTICLE PDF</span>
              </a>
            </div>

          </main>

          {/* ================= RIGHT SIDEBAR (~32% width) - NO BORDERS, ROUNDED-3XL ================= */}
          <aside className="lg:col-span-4 space-y-6">

            {/* Card 1: Article Information */}
            <div className="bg-white rounded-3xl p-7 shadow-sm">
              <h3 className="text-sm font-bold text-[#03478E] pb-3 mb-2 uppercase tracking-wider">
                Article Information
              </h3>

              <div className="divide-y divide-gray-100 text-sm">
                <div className="py-2.5 flex justify-between gap-2">
                  <span className="text-gray-500 font-medium">Article Type</span>
                  <span className="font-bold text-gray-800 text-right">{articleTypeDisplay}</span>
                </div>
                <div className="py-2.5 flex justify-between gap-2">
                  <span className="text-gray-500 font-medium">Volume</span>
                  <span className="font-bold text-gray-800 text-right">{volNumber}</span>
                </div>
                <div className="py-2.5 flex justify-between gap-2">
                  <span className="text-gray-500 font-medium">Issue</span>
                  <span className="font-bold text-gray-800 text-right">{issNumber}</span>
                </div>
                <div className="py-2.5 flex justify-between gap-2">
                  <span className="text-gray-500 font-medium">Publication Period</span>
                  <span className="font-bold text-gray-800 text-right">{issuePeriod}</span>
                </div>
                {formatArticlePages(article) && (
                  <div className="py-2.5 flex justify-between gap-2">
                    <span className="text-gray-500 font-medium">Pages</span>
                    <span className="font-bold text-gray-800 text-right">{formatArticlePages(article)}</span>
                  </div>
                )}
                {[
                  ['Received', article.receivedDate],
                  ['Resubmitted', article.revisedDate],
                  ['Accepted', article.acceptedDate],
                  ['Final Acceptance', article.finalAcceptedDate],
                  ['Published', article.publishedDate || article.date],
                ]
                  .filter(([, value]) => formatDisplayDate(value))
                  .map(([label, value]) => (
                    <div key={label} className="py-2.5 flex justify-between gap-2">
                      <span className="text-gray-500 font-medium">{label}</span>
                      <span className="font-bold text-gray-800 text-right">{formatDisplayDate(value)}</span>
                    </div>
                  ))}
                <div className="py-2.5 flex justify-between gap-2">
                  <span className="text-gray-500 font-medium">Language</span>
                  <span className="font-bold text-gray-800 text-right">English</span>
                </div>
                <div className="py-2.5 flex justify-between gap-2">
                  <span className="text-gray-500 font-medium">Peer Review</span>
                  <span className="font-bold text-gray-800 text-right">Double-Blind Peer Review</span>
                </div>
              </div>
            </div>

            {/* Card 2: How to Cite (Vancouver), between Article Information and Publisher Information */}
            {(() => {
              const citation = vancouverCitation({
                authors: (Array.isArray(article.authorsList) && article.authorsList.length > 0)
                  ? article.authorsList
                  : (article.authors || article.authorName),
                title: article.title,
                year: (issuePeriod.match(/\d{4}/) || [String(article.date || '').slice(0, 4)])[0],
                volume: volNumber,
                issue: issNumber,
                pages: article.pages || article.pageRange || getArticlePages(article).range.replace('–', '-'),
                doi: article.doi,
                url: `https://aimjhm.com${window.location.pathname.replace(/\/+$/, '')}`,
              });
              const copy = () => {
                const done = () => {
                  setCitationCopied(true);
                  setTimeout(() => setCitationCopied(false), 2000);
                };
                // Older browsers / non-https: fall back to a hidden textarea
                const fallback = () => {
                  const ta = document.createElement('textarea');
                  ta.value = citation;
                  ta.setAttribute('readonly', '');
                  ta.style.position = 'fixed';
                  ta.style.opacity = '0';
                  document.body.appendChild(ta);
                  ta.select();
                  try {
                    if (document.execCommand('copy')) done();
                  } catch (e) {
                    // Nothing more we can do
                  }
                  document.body.removeChild(ta);
                };
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(citation).then(done).catch(fallback);
                } else {
                  fallback();
                }
              };
              return (
                <div className="bg-white rounded-3xl p-7 shadow-sm">
                  <div className="flex items-center justify-between gap-3 pb-3 mb-1">
                    <h3 className="text-sm font-bold text-[#03478E] uppercase tracking-wider flex items-center gap-2">
                      <FaQuoteLeft className="w-3 h-3" />
                      How to Cite
                      <span className="text-xs font-semibold normal-case tracking-normal text-gray-500">(Vancouver)</span>
                    </h3>
                    <button
                      type="button"
                      onClick={copy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full bg-blue-50 text-[#03478E] hover:bg-[#03478E] hover:text-white transition"
                    >
                      {citationCopied ? <FaCheck className="w-2.5 h-2.5" /> : <FaCopy className="w-2.5 h-2.5" />}
                      {citationCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  {/* One click on the citation copies it */}
                  <button
                    type="button"
                    onClick={copy}
                    title="Click to copy"
                    className={`group w-full text-left rounded-2xl p-4 border transition cursor-pointer ${
                      citationCopied ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-transparent hover:border-gray-300'
                    }`}
                  >
                    <span className="text-sm text-default leading-relaxed italic break-words block">{citation}</span>
                    <span className={`mt-2 block text-xs font-semibold ${citationCopied ? 'text-emerald-700' : 'text-gray-400 group-hover:text-gray-600'}`}>
                      {citationCopied ? 'Copied to clipboard' : 'Click to copy'}
                    </span>
                  </button>
                </div>
              );
            })()}

            {/* Card 3: Publisher Information (Screen 7 Mockup) */}
            <div className="bg-white rounded-3xl p-7 shadow-sm text-sm space-y-3">
              <h3 className="text-sm font-bold text-[#03478E] pb-2 mb-2 uppercase tracking-wider">
                Publisher Information
              </h3>

              <div>
                <p className="font-bold text-gray-800">{publisher.name}</p>
                {publisher.imprint && <p className="text-xs text-gray-600">{publisher.imprint}</p>}
              </div>

              <div className="text-xs text-gray-600 space-y-1">
                <p className="font-semibold text-gray-700">Registered Office:</p>
                <p>{publisher.address}</p>
              </div>

              <div className="pt-2 text-xs text-[#03478E] space-y-1">
                <p><a href="mailto:editor@aimjhm.com" className="hover:underline">editor@aimjhm.com</a></p>
                <p><a href="mailto:admin@domusmediverse.com" className="hover:underline">admin@domusmediverse.com</a></p>
                <p><a href="mailto:domus.publications@gmail.com" className="hover:underline">domus.publications@gmail.com</a></p>
                <p><a href="https://aimjhm.com" className="hover:underline">aimjhm.com</a></p>
              </div>
            </div>

          </aside>

        </div>
      </div>



    </div>
  );
};

export default ArticleDetail;
